// src/components/Sidebar/sidebarMenu.js
// ---------------------------------------------------------------------------
// Single source of truth for sidebar navigation. Routes, roles and nesting are
// copied 1:1 from the previous Sidebar.jsx - only the grouping (sections) and
// icons changed. Only modules that already exist in App.js are listed.
//
//   countKey    -> key into the `notificationCounts` object from AppLayout
//   activePaths -> route prefixes that should highlight the item
// ---------------------------------------------------------------------------
import {
  LayoutDashboard,
  BarChart3,
  Mail,
  Users,
  Network,
  Briefcase,
  UserPlus,
  TrendingUp,
  Wallet,
  LogOut,
  Settings,
} from "lucide-react";

const ALL_ROLES = ["ROLE_ADMIN", "ROLE_HR", "ROLE_MANAGER", "ROLE_EMPLOYEE"];

export const SIDEBAR_SECTIONS = [
  {
    id: "main",
    title: "Main",
    items: [
      {
        name: "Dashboard",
        icon: LayoutDashboard,
        to: "/dashboard",
        countKey: "dashboard",
        roles: ["ROLE_EMPLOYEE"],
      },
    ],
  },
  {
    id: "self-service",
    title: "Self Service",
    items: [
      {
        name: "ESS",
        icon: Users,
        to: "/ess/my-leave",
        countKey: "ess",
        activePaths: ["/ess"],
        roles: ALL_ROLES,
        submenu: [
          {
            name: "Leave",
            to: "/ess/my-leave",
            roles: ALL_ROLES,
            submenu: [
              { name: "My Leave", to: "/ess/my-leave", roles: ALL_ROLES },
              { name: "Apply Leave", to: "/ess/apply-leave", roles: ALL_ROLES },
              { name: "Fallback", to: "/ess/fallback", roles: ALL_ROLES },
            ],
          },
          {
            name: "Attendance",
            to: "/ess/my-logs",
            roles: ALL_ROLES,
            submenu: [
              { name: "My Logs", to: "/ess/my-logs", roles: ALL_ROLES },
              {
                name: "My Time Sheet",
                to: "/ess/my-time-sheet",
                roles: ALL_ROLES,
              },
            ],
          },
          {
            name: "Reimbursements",
            to: "/ess/reimbursements",
            roles: ALL_ROLES,
          },
          {
            name: "Emergency Funds",
            to: "/ess/emergency-funds",
            roles: ALL_ROLES,
          },
          { name: "Payroll", to: "/ess/payroll", roles: ALL_ROLES },
          {
            name: "List Of Holidays",
            to: "/ess/list-of-holidays",
            roles: ALL_ROLES,
          },
          { name: "Policy", to: "/ess/Policy", roles: ALL_ROLES },
        ],
      },
    ],
  },
  {
    id: "team",
    title: "Team Management",
    items: [
      {
        name: "Teams",
        icon: Network,
        to: "/team/team-leave-apply",
        countKey: "teams",
        activePaths: ["/team"],
        roles: ["ROLE_MANAGER", "ROLE_HR", "ROLE_ADMIN"],
        submenu: [
          {
            name: "Mark Attendance",
            to: "/team/mark-attendance",
            roles: ["ROLE_MANAGER", "ROLE_ADMIN"],
          },
          {
            name: "Dashboard",
            to: "/team/team-logs",
            roles: ["ROLE_MANAGER"],
          },
          {
            name: "Manage Leave",
            to: "/team/manage-team-leave",
            roles: ["ROLE_MANAGER"],
          },
          {
            name: "Manage Fallback",
            to: "/team/team-fallback",
            roles: ["ROLE_MANAGER"],
          },
          {
            name: "Apply Leave",
            to: "/team/team-leave-apply",
            roles: ["ROLE_MANAGER", "ROLE_ADMIN", "ROLE_HR"],
          },
          {
            name: "Manage Attendance",
            to: "/team/manage-attendance",
            roles: ["ROLE_MANAGER"],
          },
          {
            name: "Time Sheet",
            to: "/team/team-time-sheet",
            roles: ["ROLE_MANAGER"],
          },
          {
            name: "Loan Approval",
            to: "/team/team-loan-approval",
            roles: ["ROLE_HR"],
          },
          {
            name: "Payroll",
            to: "/team/comp-payroll",
            roles: ["ROLE_HR"],
          },
          {
            name: "Reimbursements",
            to: "/team/team-reimbursements",
            roles: ["ROLE_MANAGER"],
          },
        ],
      },
    ],
  },
  {
    id: "talent",
    title: "Talent Management",
    items: [
      {
        name: "Recruitments",
        icon: Briefcase,
        to: "/recruitment",
        countKey: "recruitment",
        activePaths: ["/recruitment"],
        roles: ["ROLE_HR"],
        submenu: [
          {
            name: "Candidates",
            to: "/recruitment/candidates",
            roles: ["ROLE_HR"],
          },
          { name: "Vendors", to: "/recruitment/vendors", roles: ["ROLE_HR"] },
          {
            name: "Recruitments",
            to: "/recruitment/recruitments",
            roles: ["ROLE_HR"],
          },
          { name: "Jobs", to: "/recruitment/jobs", roles: ["ROLE_HR"] },
          { name: "Walkin", to: "/recruitment/walkin", roles: ["ROLE_HR"] },
          { name: "Timeline", to: "/recruitment/timeline", roles: ["ROLE_HR"] },
        ],
      },
      {
        name: "On-Boarding",
        icon: UserPlus,
        to: "/on-boarding",
        countKey: "onboarding",
        activePaths: ["/on-boarding", "/onboarding"],
        roles: ["ROLE_HR"],
        submenu: [
          {
            name: "On-Boarding Repository",
            to: "/onboarding/onboarding-repository",
            roles: ["ROLE_HR"],
          },
          {
            name: "Check Lists",
            to: "/onboarding/document-checklist",
            roles: ["ROLE_HR"],
            submenu: [
              {
                name: "Document Checklist",
                to: "/onboarding/document-checklist",
                roles: ["ROLE_HR"],
              },
              {
                name: "Assets Checklist",
                to: "/onboarding/assets-checklist",
                roles: ["ROLE_HR"],
              },
              {
                name: "HR Checklist",
                to: "/onboarding/hr-checklist",
                roles: ["ROLE_HR"],
              },
            ],
          },
          {
            name: "Assign Mentor",
            to: "/onboarding/assign-mentor",
            roles: ["ROLE_HR"],
          },
        ],
      },
      {
        name: "Performance Management",
        icon: TrendingUp,
        to: "/performance-management/repository",
        countKey: "performance",
        activePaths: ["/performance-management"],
        roles: ["ROLE_HR", "ROLE_MANAGER"],
        submenu: [
          {
            name: "Repository",
            to: "/performance-management/repository",
            roles: ["ROLE_HR", "ROLE_MANAGER"],
          },
          {
            name: "My Mindset Report",
            to: "/performance-management/my-mindset-report",
            roles: ["ROLE_HR", "ROLE_MANAGER"],
          },
          {
            name: "Review Parameters",
            to: "/performance-management/goals-kra-assessment-master",
            roles: ["ROLE_HR", "ROLE_MANAGER"],
            submenu: [
              {
                name: "Goals / KRA Assessment Master",
                to: "/performance-management/goals-kra-assessment-master",
                roles: ["ROLE_HR", "ROLE_MANAGER"],
              },
              {
                name: "Skills Assessment Master",
                to: "/performance-management/skills-assessment-master",
                roles: ["ROLE_HR", "ROLE_MANAGER"],
              },
            ],
          },
          {
            name: "Appraisal",
            to: "/performance-management/my-assessments",
            roles: ["ROLE_HR", "ROLE_MANAGER"],
            submenu: [
              {
                name: "My Assessments",
                to: "/performance-management/my-assessments",
                roles: ["ROLE_HR", "ROLE_MANAGER"],
              },
              {
                name: "Team Assessments",
                to: "/performance-management/team-assessments",
                roles: ["ROLE_HR", "ROLE_MANAGER"],
              },
              {
                name: "Appraisal Report",
                to: "/performance-management/appraisal-report",
                roles: ["ROLE_HR", "ROLE_MANAGER"],
              },
            ],
          },
          {
            name: "Performance Report",
            to: "/performance-management/performance-report",
            roles: ["ROLE_HR", "ROLE_MANAGER"],
          },
        ],
      },
      {
        name: "Off-Boarding",
        icon: LogOut,
        to: "/offboarding/repository",
        countKey: "offboarding",
        activePaths: ["/offboarding"],
        roles: ["ROLE_HR"],
        submenu: [
          {
            name: "Repository",
            to: "/offboarding/repository",
            roles: ["ROLE_HR"],
          },
          {
            name: "Off-Board Check List",
            to: "/offboarding/off-board-check-list",
            roles: ["ROLE_HR"],
          },
          {
            name: "Apply Resignation",
            to: "/offboarding/apply-resignation",
            roles: ["ROLE_HR"],
          },
          {
            name: "Resignation Applicants",
            to: "/offboarding/resignation-applicants",
            roles: ["ROLE_HR"],
          },
        ],
      },
    ],
  },
  {
    id: "payroll-finance",
    title: "Payroll & Finance",
    items: [
      {
        name: "Compensation / Benefits",
        icon: Wallet,
        to: "/compensation-benefits/payroll-report/salary-register",
        countKey: "compensation",
        activePaths: ["/compensation-benefits"],
        roles: ["ROLE_ADMIN", "ROLE_HR", "ROLE_ACCOUNTANT"],
        submenu: [
          {
            name: "Payroll Report",
            to: "/compensation-benefits/payroll-report/salary-register",
            roles: ["ROLE_ADMIN", "ROLE_HR", "ROLE_ACCOUNTANT"],
            submenu: [
              {
                name: "Salary Register",
                to: "/compensation-benefits/payroll-report/salary-register",
                roles: ["ROLE_ADMIN", "ROLE_HR", "ROLE_ACCOUNTANT"],
              },
              {
                name: "Salary Budget",
                to: "/compensation-benefits/payroll-report/salary-budget",
                roles: ["ROLE_ADMIN", "ROLE_HR", "ROLE_ACCOUNTANT"],
              },
              {
                name: "Payroll Summary",
                to: "/compensation-benefits/payroll-report/summary",
                roles: ["ROLE_ADMIN", "ROLE_HR", "ROLE_ACCOUNTANT"],
              },
            ],
          },
          {
            name: "Payroll",
            to: "/compensation-benefits/payroll",
            roles: ["ROLE_ADMIN", "ROLE_ACCOUNTANT"],
          },
        ],
      },
    ],
  },
  {
    id: "communication",
    title: "Communication",
    items: [
      {
        name: "Email Automation",
        icon: Mail,
        to: "/email-automation",
        countKey: "emailAutomation",
        activePaths: ["/email-automation"],
        roles: ["ROLE_ADMIN", "ROLE_HR"],
      },
    ],
  },
  {
    id: "insights",
    title: "Insights",
    items: [
      {
        name: "Analytics",
        icon: BarChart3,
        to: "/analytics",
        countKey: "analytics",
        activePaths: ["/analytics"],
        roles: ["ROLE_ADMIN", "ROLE_HR", "ROLE_MANAGER"],
      },
    ],
  },
];

// Bottom of the sidebar. "Help & Support" is intentionally absent: there is
// no such route in App.js, and dead links are not allowed.
export const SIDEBAR_FOOTER_ITEMS = [
  {
    name: "Settings",
    icon: Settings,
    to: "/account-settings",
    activePaths: ["/account-settings"],
    roles: [...ALL_ROLES, "ROLE_ACCOUNTANT"],
  },
];

// ---------------------------------------------------------------------------
// Helpers shared by Sidebar.jsx and SidebarItem.jsx
// ---------------------------------------------------------------------------

// Case-insensitive, trailing-slash-insensitive (react-router v6 matches
// case-insensitively, so "/ess/policy" must highlight "/ess/Policy").
export const normalizePath = (path = "") =>
  path.toLowerCase().replace(/\/+$/, "") || "/";

export const canSee = (entry, userRoles = []) =>
  !entry.roles || entry.roles.some((role) => userRoles.includes(role));

// True if `entry` or any descendant points at the current path.
export const branchMatches = (entry, pathname) =>
  normalizePath(pathname) === normalizePath(entry.to) ||
  (entry.submenu || []).some((child) => branchMatches(child, pathname));

export const isItemActive = (item, pathname) => {
  if (branchMatches(item, pathname)) return true;
  const current = normalizePath(pathname);
  return (item.activePaths || []).some(
    (prefix) => current === prefix || current.startsWith(`${prefix}/`),
  );
};
